import { z } from 'zod'

export const filterWizardTypeOptionsSchema = z.enum(
  ['tree', 'url']
)

export const buttonVariantSchema = z.enum(
  ['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']
)

export const imageFitSchema = z.enum(
  ['cover', 'contain', 'fill', 'none', 'scale-down']
)

export const navigationButtonsSchema = z.object({
  previous_variant: buttonVariantSchema.optional(),
  next_variant: buttonVariantSchema.optional(),
  previous_icon: z.string().optional(),
  next_icon: z.string().optional(),
}).optional()

export const imageDisplaySchema = z.object({
  height: z.number().optional(),
  width: z.number().optional(),
  fit: imageFitSchema.optional(),
}).optional()

export const compactFilterAttributeSchema = z.object({
  id: z.number(),
  value_ids: z.array(z.number())
}).transform(({
  value_ids,
  ...rest
}) => ({
  valueIds: value_ids,
  ...rest
}))

export const filterWizardFilterSchema = z.object({
  name: z.string(),
  display_type: z.enum(['default', 'image']).nullable(),
  sort_mode: z.string().nullable(),
  sort_direction: z.string().nullable(),
  attribute: compactFilterAttributeSchema,
}).transform(({
  display_type,
  sort_mode,
  sort_direction,
  ...rest
}) => ({
  displayType: display_type || 'default',
  sortMode: sort_mode || 'priority',
  sortDirection: sort_direction || 'desc',
  ...rest
}))

export const filterWizardFiltersSchema = z.object({
  filters: z.array(filterWizardFilterSchema)
}).transform(({
  filters,
}) => ([...filters]))

export const filterWizardConfigSchema = z.object({
  name: z.string(),
  description: z.string(),
  type: filterWizardTypeOptionsSchema,
  sort_mode: z.string().nullable(),
  sort_direction: z.string().nullable(),
  navigation_buttons: navigationButtonsSchema,
  image_display: imageDisplaySchema,
}).transform(({
  sort_mode,
  sort_direction,
  navigation_buttons,
  image_display,
  ...rest
}) => ({
  sortMode: sort_mode || 'priority',
  sortDirection: sort_direction || 'desc',
  navigationButtons: navigation_buttons ? {
    previousVariant: navigation_buttons.previous_variant || 'primary',
    nextVariant: navigation_buttons.next_variant || 'primary',
    ...(navigation_buttons.previous_icon !== undefined && { previousIcon: navigation_buttons.previous_icon }),
    ...(navigation_buttons.next_icon !== undefined && { nextIcon: navigation_buttons.next_icon }),
  } : undefined,
  imageDisplay: image_display ? {
    ...(image_display.height !== undefined && { height: image_display.height }),
    ...(image_display.width !== undefined && { width: image_display.width }),
    ...(image_display.fit !== undefined && { fit: image_display.fit }),
  } as ImageDisplayConfig : undefined,
  ...rest
}))

export type FilterWizardType = z.infer<typeof filterWizardTypeOptionsSchema>
export type SortModeOptions = 'priority' | 'code' | 'price' | 'rating' | 'name' | 'seq'
export type SortDirectionOptions = 'asc' | 'desc'
export type ButtonVariant = z.infer<typeof buttonVariantSchema>
export type ImageFit = z.infer<typeof imageFitSchema>
export type FilterWizardConfig = z.infer<typeof filterWizardConfigSchema>
export type FilterWizardFilters = z.infer<typeof filterWizardFiltersSchema>

export type NavigationButtonsConfig = {
  previousVariant: ButtonVariant
  nextVariant: ButtonVariant
  previousIcon?: string
  nextIcon?: string
}

export type ImageDisplayConfig = {
  height?: number
  width?: number
  fit?: ImageFit
}

export const paperVariantSchema = z.enum(
  ['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']
)

export const paperSizeSchema = z.enum(
  ['default', 'large', 'extraLarge']
)

export type PaperVariant = z.infer<typeof paperVariantSchema>
export type PaperSize = z.infer<typeof paperSizeSchema>

export type PaperConfig = {
  variant?: PaperVariant
  size?: PaperSize
  bordered?: boolean
}

export type FilterWizardDisplayOptions = {
  navigationButtons?: Partial<NavigationButtonsConfig>
  imageDisplay?: ImageDisplayConfig
  paper?: PaperConfig
}

export type FilterNavigationDisplayOptions = {
  paper?: PaperConfig
  showNavIcons?: boolean
}

export type FilterHeaderDisplayOptions = {
  titleVariant?: string
  nameVariant?: string
}
